import { api, getApiErrorMessage } from "../../helpers";
import logToSentry from "../../helpers/log-to-sentry";

import { URL_API_CATEGORIES } from "../../constants";

import dayjs from "dayjs";

/**
 * Action types
 * @property {string} CATEGORIES_ERROR
 * @property {string} CATEGORIES_LOADING
 * @property {string} CATEGORIES_SUCCESS
 */
export const types = {
  CATEGORIES_ERROR: "CATEGORIES_ERROR",
  CATEGORIES_LOADING: "CATEGORIES_LOADING",
  CATEGORIES_SUCCESS: "CATEGORIES_SUCCESS",
};

/**
 * @namespace Reducers
 */
export const reducers = {
  /**
   * Updates categories state
   * @param {object} state
   * @param {object} action
   * @param {string} action.type - `CATEGORIES_SUCCESS`, or no change
   * @param {object} action.categories
   * @param {array} action.categories.search
   * @param {tree} action.categories.tree
   * @memberof Reducers
   */
  categories: (state = { search: [], tree: [] }, action) => {
    switch (action.type) {
      case types.CATEGORIES_SUCCESS:
        return action.categories;
      default:
        return state;
    }
  },

  /**
   * Updates categories count state—
   * used to assess if categories need to be fetched from the API
   * @param {object} state
   * @param {object} action
   * @param {string} action.type - `CATEGORIES_SUCCESS`, or no change
   * @param {object} action.categories
   * @param {array} action.categories.search
   * @memberof Reducers
   */
  categoriesCount: (state = 0, action) => {
    switch (action.type) {
      case types.CATEGORIES_SUCCESS:
        return action.categories.search.length;
      default:
        return state;
    }
  },

  /**
   * Updates categories error state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.error
   * @memberof Reducers
   */
  categoriesError: (state = null, action) => {
    switch (action.type) {
      case types.CATEGORIES_ERROR:
        return action.error;
      case types.CATEGORIES_LOADING:
      case types.CATEGORIES_SUCCESS:
        return null;
      default:
        return state;
    }
  },

  /**
   * Updates categories expires state—
   * used to assess if categories need to be updated from the API
   * @param {object} state
   * @param {object} action
   * @param {string} action.type - `CATEGORIES_SUCCESS`, or no change
   * @memberof Reducers
   */
  categoriesExpires: (state = null, action) => {
    switch (action.type) {
      case types.CATEGORIES_SUCCESS:
        return dayjs().add(1, "days").format();
      default:
        return state;
    }
  },

  /**
   * Updated categories loading state
   * @param {boolean} state
   * @param {object} action
   * @param {string} action.type
   * @memberof Reducers
   */
  categoriesLoading: (state = false, action) => {
    switch (action.type) {
      case types.CATEGORIES_LOADING:
        return true;
      case types.CATEGORIES_ERROR:
      case types.CATEGORIES_SUCCESS:
        return false;
      default:
        return state;
    }
  },
};

/**
 * @namespace Actions
 */
export const actions = {
  /**
   * Fetch categories from API
   * @memberof Actions
   */
  fetch: () => dispatch => {
    dispatch(actions.handleLoading());

    return api
      .get(URL_API_CATEGORIES)
      .then(response => {
        if (
          response &&
          response.data &&
          response.data.data &&
          Array.isArray(response.data.data)
        ) {
          const categories = formatCategories(response.data.data);
          dispatch(actions.handleSuccess(categories));
        } else {
          dispatch(actions.parseError(response));
        }
      })
      .catch(error => {
        dispatch(actions.parseError(error));
      });
  },

  /**
   * Alternative fetch method that includes the logic to check
   * if categories have already been fetched
   * @memberof Actions
   */
  fetchIfNeeded: () => async (dispatch, getState) => {
    try {
      const {
        categoriesCount,
        categoriesExpires,
        categoriesLoading,
        categoriesError,
      } = getState();

      const shouldFetch =
        (!categoriesCount ||
          !categoriesExpires ||
          (!!categoriesExpires && dayjs().isAfter(categoriesExpires))) &&
        !categoriesLoading &&
        !categoriesError;

      if (!shouldFetch) {
        return "skipped";
      }

      dispatch(actions.handleLoading());

      const response = await api.get(URL_API_CATEGORIES);

      if (!response?.data?.data || !Array.isArray(response.data.data)) {
        dispatch(actions.parseError(response));
        return "invalid";
      }

      const categories = formatCategories(response.data.data);
      dispatch(actions.handleSuccess(categories));

      return "success";
    } catch (err) {
      dispatch(actions.parseError(err));
      return "error";
    }
  },

  /**
   * Handle categories error messages
   * @param {string} message
   * @memberof Actions
   */
  handleError: message => ({
    type: types.CATEGORIES_ERROR,
    error: message,
  }),

  /**
   * Handle categories loadng events
   * @memberof Actions
   */
  handleLoading: () => ({
    type: types.CATEGORIES_LOADING,
  }),

  /**
   * Handle categories success
   * @param {array} categories
   * @memberof Actions
   */
  handleSuccess: categories => ({
    type: types.CATEGORIES_SUCCESS,
    categories: categories,
  }),

  /**
   * Lookup categories
   * @param {number} categoryId
   * @param {boolean} asObj
   * @memberof Actions
   */
  lookup: (categoryId, asObj) => (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const { categories } = getState();

      const findCategory = id => {
        let found = [];
        const cat = categories.search.find(c => c.id === id);
        if (cat && !asObj) {
          found = [...found, cat.name, ...findCategory(cat.parent_id)];
        } else if (cat && asObj) {
          found = [
            ...found,
            { id, name: cat.name, parent_id: cat.parent_id },
            ...findCategory(cat.parent_id),
          ];
        }
        return found;
      };

      const category = findCategory(categoryId);

      if (category.length > 0) {
        resolve({
          success: true,
          category: category.reverse(),
        });
      } else {
        const message = `Unknown category (ID: ${categoryId})`;
        logToSentry(message, "warning");

        reject({
          success: false,
          message,
        });
      }
    });
  },

  /**
   * Parse errors and dispatch error message
   * @param {*} error
   * @memberof Actions
   */
  parseError: error => dispatch => {
    const message = getApiErrorMessage(error);
    dispatch(actions.handleError(message));
  },
};

/**
 * Format categories data
 *
 * @param {array} data
 * @returns {object}
 */
function formatCategories(data) {
  const flatten = a =>
    a.concat(
      ...a.map(b => {
        if (!!b.name) {
          b.name = b.name.trim();
        }
        return flatten(b.children);
      })
    );
  const categories = {
    search: flatten(data),
    tree: data,
  };

  return categories;
}
